import {
  baseTreeGroupColumnDef,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Box,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  EXTRAS_MENU_TOOLTIP_PROPS,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  MaxOrderSizeDialogs,
  MixpanelEvent,
  SideEnum,
  Toggle,
  Tooltip,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useFavoriteSecurities,
  useMixpanel,
  usePersistedBlotterTable,
  type ColumnDynamics,
  type Column as TalosColumn,
} from '@talos/kyoko';
import type { ColDef, GetQuickFilterTextParams, GridOptions } from 'ag-grid-community';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import styled, { createGlobalStyle, type DefaultTheme } from 'styled-components';
import { CONTROL_TOOLTIPS } from '../../containers/Blotters/tooltips';
import { ClickToTradeOrderPreview } from '../MarketDataCard/ClickToTradeOrderPreview';
import { PriceGridFavoriteCellRenderer, PriceGridSymbolCellRenderer } from './cellRenderers';
import { getPriceGridAddPriceColumnGroup } from './columns/priceGridAddPriceColumn';
import { PRICE_GRID_LEG_MARKET_SELECTOR_CELL_CLASS } from './columns/priceGridLegMarketSelectorColumn';
import {
  PRICE_GRID_MARKET_DATA_BID_QTY_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_LEG_MARKET_SELECTOR_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_MARKET_STATUS_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_MENU_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_OFFER_QTY_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_PRICE_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_RFQ_COLUMN_SUFFIX,
} from './columns/priceGridMarketDataColumnUtils';
import { getPriceGridMultilegProductTypeSearchLabels, getPriceGridProductTypeSearchLabels } from './filters/options';
import {
  PRICE_GRID_LEG_MARKET_DATA_COLUMN_DYNAMICS_SPEC,
  PRICE_GRID_MARKET_DATA_COLUMN_DYNAMICS_SPEC,
  usePriceGridColumnDynamicsSpecs,
  usePriceGridMarketDataEnrichment,
  usePriceGridQuantityColumnVisibility,
} from './hooks';
import {
  PRICE_GRID_CUSTOMER_MARKET_DATA_ENRICHMENT_SPEC,
  PRICE_GRID_MARKET_DATA_ENRICHMENT_SPEC,
} from './hooks/enrichment';
import { DEFAULT_PRICE_GRID_PRICE_INCREMENT, isPriceLadderMode, PriceGridLadderType } from './ladder';
import { PriceGridRowKind, type PriceGridEntity } from './PriceGridEntity';
import { usePriceGridSettingsContext } from './PriceGridSettingsContext';
import type { PriceGridProps } from './types';
import { formatPriceGridGroupColumnValue, getPriceGridGroupColumnValue, priceGridTreeGroupComparator } from './utils';

const getClickToTradeRowBackground = (side: SideEnum, theme: DefaultTheme) =>
  side === SideEnum.Buy ? theme.colors.green.dim : theme.colors.red.dim;

const getClickToTradeTextBackground = (side: SideEnum, theme: DefaultTheme) =>
  `color-mix(in srgb, ${side === SideEnum.Buy ? '#337E5A' : '#922E43'} 40%, ${theme.backgroundBlotterTableRow} 60%)`;

const PRICE_GRID_FAVORITE_COLUMN_ID = 'price-grid-favorite';
const PRICE_GRID_HEADER_HEIGHT = 28;
const PRICE_GRID_CHILD_HEADER_HEIGHT = 0;
const PRICE_GRID_KNOWLEDGE_BASE_URL = 'https://kb.talostrading.com/principal-trading/navigating-trading-ui/price-grid';
const PRICE_GRID_KNOWLEDGE_BASE_TOOLTIP = 'View the Price Grid Knowledge Base page.';
const PRICE_GRID_MARKET_DATA_LEAF_COLUMN_SUFFIXES = [
  PRICE_GRID_MARKET_DATA_PRICE_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_BID_QTY_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_OFFER_QTY_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_RFQ_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_LEG_MARKET_SELECTOR_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_MARKET_STATUS_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_MENU_COLUMN_SUFFIX,
];

function PriceGridKnowledgeBaseButton() {
  const mixpanel = useMixpanel();

  const handleClick = () => {
    mixpanel.track(MixpanelEvent.OpenKnowledgeBase);
    window.open(PRICE_GRID_KNOWLEDGE_BASE_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <Tooltip tooltip={PRICE_GRID_KNOWLEDGE_BASE_TOOLTIP}>
      <IconButton
        ghost
        icon={IconName.BookOpen}
        size={FormControlSizes.Small}
        aria-label={PRICE_GRID_KNOWLEDGE_BASE_TOOLTIP}
        data-testid="price-grid-knowledge-base-button"
        onClick={handleClick}
      />
    </Tooltip>
  );
}

export function getPriceGridHeaderHeights(
  gridOptions: Pick<GridOptions<PriceGridEntity>, 'groupHeaderHeight' | 'headerHeight'> | undefined
) {
  return {
    groupHeaderHeight: gridOptions?.groupHeaderHeight ?? gridOptions?.headerHeight ?? PRICE_GRID_HEADER_HEIGHT,
    headerHeight:
      gridOptions?.groupHeaderHeight != null
        ? (gridOptions.headerHeight ?? PRICE_GRID_CHILD_HEADER_HEIGHT)
        : PRICE_GRID_CHILD_HEADER_HEIGHT,
  };
}

const PRICE_GRID_CTT_TEXT_LEFT_OFFSET_PX = 36;

const ClickToTradePreviewOverlay = styled(Box)<{ $side: SideEnum; $top: number }>`
  align-items: center;
  color: ${({ $side, theme }) => ($side === SideEnum.Buy ? theme.colors.green.lighten : theme.colors.red.lighten)};
  display: flex;
  font-size: ${({ theme }) => theme.fontSizeMd}rem;
  font-weight: ${({ theme }) => theme.fontWeightMedium};
  height: var(--ag-row-height);
  left: 0;
  line-height: ${({ theme }) => theme.lineHeightMd}rem;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: ${({ $top }) => $top}px;
  z-index: 2;
`;

const ClickToTradePreviewContent = styled.div<{ $side: SideEnum }>`
  align-items: center;
  background-color: ${({ $side, theme }) => getClickToTradeTextBackground($side, theme)};
  color: inherit;
  display: flex;
  gap: ${({ theme }) => theme.spacing4}px;
  padding: ${({ theme }) => theme.spacing8}px;
  padding-left: ${({ theme }) => PRICE_GRID_CTT_TEXT_LEFT_OFFSET_PX + theme.spacing16}px;
  white-space: nowrap;

  span,
  i {
    color: inherit;
  }
`;

const PriceGridGlobalStyles = createGlobalStyle`
  .ag-header-cell.price-grid-market-data-child-header {
    background: transparent;
    border-color: transparent;
    pointer-events: none;

    .ag-header-cell-comp-wrapper {
      display: none;
    }

    &:hover,
    &.ag-header-active,
    &.ag-column-hover,
    &.ag-column-menu-visible {
      background: transparent;
    }
  }

  .ag-popup-child {
    &:has(.price-grid-market-data-group-tool-panel) {
      &.ag-ltr .ag-column-select-add-group-indent {
        margin-left: 0;
      }

      &.ag-rtl .ag-column-select-add-group-indent {
        margin-right: 0;
      }

      .ag-column-select-virtual-list-container {
        height: auto !important;
      }

      .ag-column-select-virtual-list-item {
        position: static !important;
        transform: none !important;
      }
    }

    .ag-column-select-column-group.price-grid-market-data-group-tool-panel {
      .ag-column-group-icons {
        display: none;
      }
    }

    .ag-column-select-virtual-list-item:has(
        .ag-column-select-column .ag-column-select-column-label:empty
      ) {
      height: 0 !important;
      min-height: 0 !important;
      overflow: hidden;
    }
  }

  .ag-row.price-grid-click-to-trade-buy .ag-cell {
    background-color: ${({ theme }) => getClickToTradeRowBackground(SideEnum.Buy, theme)};
  }

  .ag-row.price-grid-click-to-trade-sell .ag-cell {
    background-color: ${({ theme }) => getClickToTradeRowBackground(SideEnum.Sell, theme)};
  }

  .ag-row.price-grid-click-to-trade-buy,
  .ag-row.price-grid-click-to-trade-sell {
    .ag-group-contracted,
    .ag-group-expanded,
    .ag-group-value,
    .ag-row-group-leaf-indent {
      visibility: hidden;
    }
  }

  .ag-cell.${PRICE_GRID_LEG_MARKET_SELECTOR_CELL_CLASS} {
    align-items: center;
    display: flex;
  }
`;

function getDataPath(entity: PriceGridEntity) {
  return entity.dataPath;
}

function getPriceGridQuickFilterText(params: GetQuickFilterTextParams<PriceGridEntity, string>) {
  if (!params.data) {
    return params.value ?? '';
  }

  const { security } = params.data;
  return [
    params.value,
    security.Symbol,
    security.DisplaySymbol,
    security.Description,
    ...getPriceGridProductTypeSearchLabels(security.ProductType),
    ...getPriceGridMultilegProductTypeSearchLabels(security),
    security.Expiration,
  ]
    .filter((value): value is string => value != null && value !== '')
    .join('__');
}

function getPriceGridFavoriteColumn(
  favoriteSymbols: Set<string>
): TalosColumn<ColDef<PriceGridEntity>, PriceGridEntity> {
  return {
    type: 'custom',
    id: PRICE_GRID_FAVORITE_COLUMN_ID,
    title: '',
    pinned: 'left',
    initialPinned: 'left',
    lockPosition: 'left',
    initialWidth: 44,
    sortable: true,
    suppressHeaderMenuButton: true,
    suppressColumnsToolPanel: true,
    params: {
      colId: PRICE_GRID_FAVORITE_COLUMN_ID,
      headerName: '',
      valueGetter: ({ data }) => (data?.isParentRow ? favoriteSymbols.has(data.security.Symbol) : false),
      cellRenderer: PriceGridFavoriteCellRenderer,
      comparator: (a: boolean, b: boolean) => (a === b ? 0 : a ? 1 : -1),
    },
  };
}

type PriceGridPersistence = NonNullable<PriceGridProps['persistence']>;
type PriceGridPersistedState = PriceGridPersistence['state'];

function getNormalizedPriceGridColumnDynamicsColID(dynamics: ColumnDynamics) {
  if (
    dynamics.spec !== PRICE_GRID_MARKET_DATA_COLUMN_DYNAMICS_SPEC &&
    dynamics.spec !== PRICE_GRID_LEG_MARKET_DATA_COLUMN_DYNAMICS_SPEC
  ) {
    return dynamics.colID;
  }

  for (const suffix of PRICE_GRID_MARKET_DATA_LEAF_COLUMN_SUFFIXES) {
    const leafSuffix = `-${suffix}`;
    if (dynamics.colID.endsWith(leafSuffix)) {
      return dynamics.colID.slice(0, -leafSuffix.length);
    }
  }

  return dynamics.colID;
}

function cleanPriceGridPersistedState(state: PriceGridPersistedState): PriceGridPersistedState {
  const columnDynamics = state?.extraGridState?.columnDynamics;
  if (!state || !columnDynamics?.length) {
    return state;
  }

  // Normalize colIDs: deduplicate entries and strip any leaf-column suffixes that may
  // have been persisted by older versions of the grid (Map preserves last-write-wins).
  const byNormalizedId = new Map<string, ColumnDynamics>();
  for (const cd of columnDynamics) {
    const colID = getNormalizedPriceGridColumnDynamicsColID(cd);
    byNormalizedId.set(colID, { ...cd, colID });
  }

  return {
    ...state,
    extraGridState: {
      ...state.extraGridState,
      columnDynamics: [...byNormalizedId.values()],
    },
  };
}

export const PriceGrid = memo(function PriceGrid({
  blotterID,
  dataObservable,
  errorObservable,
  columns,
  filtersPortalId: filtersPortalIdProp,
  search,
  onSearchChange,
  supportsFavorites,
  supportsMarketDataColumnSettings = true,
  supportsLegMarketDataColumnSettings = false,
  showInitialState,
  renderInitialState,
  persistence: persistenceProp,
  gridOptions,
  priceGridMarketDataHandlers,
  clickToTradePreviewOrder,
  maxOrderSizeDialogs,
  ladderConfig,
  showBBOToggle = true,
  showRFQToggle = true,
  showLegMarketSelectorToggle = false,
}: PriceGridProps) {
  const {
    alignPricesOnDecimal,
    isBBOEnabled,
    onAlignPricesOnDecimalChange,
    onBBOEnabledChange,
    onShowLegMarketSelectorChange,
    onShowRFQChange,
    showLegMarketSelector,
    showRFQ,
  } = usePriceGridSettingsContext();
  const filtersPortalId = filtersPortalIdProp ?? `${blotterID}-price-grid-filters`;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [clickToTradePreviewTop, setClickToTradePreviewTop] = useState<number>();
  const persistence = usePersistedBlotterTable(blotterID);
  const resolvedPersistence = persistenceProp ?? persistence;
  const cleanedPersistence = useMemo(
    () => ({
      ...resolvedPersistence,
      state: cleanPriceGridPersistedState(resolvedPersistence.state),
      initialState: cleanPriceGridPersistedState(resolvedPersistence.initialState),
    }),
    [resolvedPersistence]
  );
  const resolvedLadderConfig = useMemo(
    () =>
      ladderConfig ?? {
        ladderType: PriceGridLadderType.Ladder,
        priceIncrement: DEFAULT_PRICE_GRID_PRICE_INCREMENT,
      },
    [ladderConfig]
  );
  const marketDataEnrichment = usePriceGridMarketDataEnrichment({
    dataObservable,
    marketDataSpec: PRICE_GRID_MARKET_DATA_ENRICHMENT_SPEC,
    customerMarketDataSpec: PRICE_GRID_CUSTOMER_MARKET_DATA_ENRICHMENT_SPEC,
  });
  const { columnDynamicsSpecs, addPriceGridMarketDataColumn, dialogs } = usePriceGridColumnDynamicsSpecs({
    isBBOEnabled,
    showRFQ,
    supportsLegColumn: supportsLegMarketDataColumnSettings,
    priceGridMarketDataHandlers,
    ladderConfig: resolvedLadderConfig,
  });
  const customColumnUpdate = usePriceGridQuantityColumnVisibility(resolvedLadderConfig);
  const { favoriteSecurities } = useFavoriteSecurities();
  const extrasMenuPopover = useBlotterTableExtrasMenu();
  const favoriteSymbols = useMemo(() => new Set(favoriteSecurities), [favoriteSecurities]);
  const resolvedColumns = useMemo(() => {
    const addPriceColumn = supportsMarketDataColumnSettings
      ? [getPriceGridAddPriceColumnGroup({ onAddClick: params => addPriceGridMarketDataColumn(params.api) })]
      : [];
    return [...(supportsFavorites ? [getPriceGridFavoriteColumn(favoriteSymbols)] : []), ...columns, ...addPriceColumn];
  }, [addPriceGridMarketDataColumn, columns, favoriteSymbols, supportsFavorites, supportsMarketDataColumnSettings]);
  const columnDynamicsParams = useMemo(
    () => (supportsMarketDataColumnSettings ? { columnDynamicsSpecs } : undefined),
    [columnDynamicsSpecs, supportsMarketDataColumnSettings]
  );

  const contextMenuOptions = useMemo(
    () => ({
      dataDisplayCallback: (entity: PriceGridEntity) => entity.security,
    }),
    []
  );

  const autoGroupColumnDef: GridOptions<PriceGridEntity>['autoGroupColumnDef'] = useMemo(() => {
    return {
      ...(baseTreeGroupColumnDef ?? {}),
      headerName: 'Symbol',
      suppressColumnsToolPanel: true,
      initialPinned: 'left',
      initialWidth: 220,
      comparator: priceGridTreeGroupComparator,
      valueGetter: getPriceGridGroupColumnValue,
      valueFormatter: formatPriceGridGroupColumnValue,
      filterValueGetter: getPriceGridGroupColumnValue,
      getQuickFilterText: getPriceGridQuickFilterText,
      cellRendererParams: {
        ...(baseTreeGroupColumnDef?.cellRendererParams ?? {}),
        suppressCount: true,
      },
      cellRendererSelector: params => {
        const baseSelector = baseTreeGroupColumnDef?.cellRendererSelector?.(params);
        if (baseSelector) {
          return baseSelector;
        }
        if (params.node.data?.kind === PriceGridRowKind.Parent) {
          return {
            component: 'agGroupCellRenderer',
            params: {
              ...(baseTreeGroupColumnDef?.cellRendererParams ?? {}),
              suppressCount: true,
              innerRenderer: PriceGridSymbolCellRenderer,
            },
          };
        }

        return undefined;
      },
    } as GridOptions<PriceGridEntity>['autoGroupColumnDef'];
  }, []);

  const mergedGridOptions: GridOptions<PriceGridEntity> = useMemo(
    () => ({
      ...marketDataEnrichment.gridOptionsOverlay,
      treeData: true,
      getDataPath,
      autoGroupColumnDef,
      rowBuffer: 0,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      ...gridOptions,
      rowClassRules: {
        ...gridOptions?.rowClassRules,
        'price-grid-click-to-trade-buy': params =>
          params.data?.rowID === clickToTradePreviewOrder?.rowID && clickToTradePreviewOrder?.side === SideEnum.Buy,
        'price-grid-click-to-trade-sell': params =>
          params.data?.rowID === clickToTradePreviewOrder?.rowID && clickToTradePreviewOrder?.side === SideEnum.Sell,
      },
      maintainColumnOrder: isPriceLadderMode(resolvedLadderConfig.ladderType)
        ? false
        : gridOptions?.maintainColumnOrder,
      ...getPriceGridHeaderHeights(gridOptions),
    }),
    [
      autoGroupColumnDef,
      clickToTradePreviewOrder,
      gridOptions,
      marketDataEnrichment.gridOptionsOverlay,
      resolvedLadderConfig.ladderType,
    ]
  );

  const blotterTable = useBlotterTable<PriceGridEntity>({
    rowID: 'rowID' satisfies keyof PriceGridEntity,
    dataObservable: marketDataEnrichment.enrichedDataObservable,
    errorObservable,
    columns: resolvedColumns,
    persistence: cleanedPersistence,
    columnDynamicsParams,
    customColumnUpdate,
    quickSearchParams: {
      filterText: search ?? '',
    },
    contextMenuOptions,
    gridOptions: mergedGridOptions,
  });

  useEffect(() => {
    if (!clickToTradePreviewOrder) {
      setClickToTradePreviewTop(undefined);
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      const wrapper = wrapperRef.current;
      const row = wrapper?.querySelector<HTMLElement>(`[row-id="${CSS.escape(clickToTradePreviewOrder.rowID)}"]`);
      if (!wrapper || !row) {
        setClickToTradePreviewTop(undefined);
        return;
      }

      setClickToTradePreviewTop(row.getBoundingClientRect().top - wrapper.getBoundingClientRect().top);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [clickToTradePreviewOrder]);

  const handleGridMouseLeave = useCallbackRef(() => {
    if (!clickToTradePreviewOrder) {
      return;
    }

    priceGridMarketDataHandlers?.onCancelClickToTradePreview?.({
      rowID: clickToTradePreviewOrder.rowID,
      side: clickToTradePreviewOrder.side,
    });
  });

  if (showInitialState && renderInitialState) {
    return (
      <Box flex={1} w="100%" h="100%">
        {renderInitialState()}
      </Box>
    );
  }

  return (
    <Box ref={wrapperRef} position="relative" w="100%" h="100%" onMouseLeave={handleGridMouseLeave}>
      <PriceGridGlobalStyles />
      <BlotterTableFilters
        {...blotterTable.blotterTableFiltersProps}
        portalId={filtersPortalId}
        quickFilterText={search ?? ''}
        onQuickFilterTextChanged={onSearchChange}
        showFilterBuilder={false}
        showDateRangePicker={false}
        suffix={
          <HStack gap="spacing4">
            <PriceGridKnowledgeBaseButton />
            <Box data-testid="price-grid-settings-menu">
              <BlotterTableExtrasMenu {...extrasMenuPopover}>
                <Toggle
                  tooltip={CONTROL_TOOLTIPS.alignPricesOnDecimal}
                  tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                  size={FormControlSizes.Small}
                  checked={alignPricesOnDecimal}
                  onChange={onAlignPricesOnDecimalChange}
                  label="Align Prices on the Decimal"
                  data-testid="price-grid-align-prices-on-decimal-toggle"
                />
                {showBBOToggle ? (
                  <Toggle
                    tooltip={CONTROL_TOOLTIPS.enableBBO}
                    tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                    size={FormControlSizes.Small}
                    checked={isBBOEnabled}
                    onChange={onBBOEnabledChange}
                    label="Enable Best Bid/Offer Highlighting"
                    data-testid="price-grid-enable-bbo-highlighting-toggle"
                  />
                ) : null}
                {showRFQToggle ? (
                  <Toggle
                    size={FormControlSizes.Small}
                    checked={showRFQ}
                    onChange={onShowRFQChange}
                    label="Show RFQ"
                    data-testid="price-grid-show-rfq-toggle"
                  />
                ) : null}
                {showLegMarketSelectorToggle ? (
                  <Toggle
                    tooltip={CONTROL_TOOLTIPS.showLegMarketSelector}
                    tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                    size={FormControlSizes.Small}
                    checked={showLegMarketSelector}
                    onChange={onShowLegMarketSelectorChange}
                    label="Show Leg Market Selector"
                    data-testid="price-grid-show-leg-market-selector-toggle"
                  />
                ) : null}
              </BlotterTableExtrasMenu>
            </Box>
          </HStack>
        }
      />
      <BlotterTable {...blotterTable} />
      {clickToTradePreviewOrder && clickToTradePreviewTop !== undefined ? (
        <ClickToTradePreviewOverlay $side={clickToTradePreviewOrder.side} $top={clickToTradePreviewTop}>
          <ClickToTradePreviewContent $side={clickToTradePreviewOrder.side}>
            <ClickToTradeOrderPreview
              clickToTradePreviewOrder={clickToTradePreviewOrder}
              security={clickToTradePreviewOrder.security}
            />
          </ClickToTradePreviewContent>
        </ClickToTradePreviewOverlay>
      ) : null}
      {maxOrderSizeDialogs ? <MaxOrderSizeDialogs {...maxOrderSizeDialogs} /> : null}
      {supportsMarketDataColumnSettings ? dialogs : null}
    </Box>
  );
});
